import { FC, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import { CircularProgress } from '@material-ui/core'
import { Error } from '@material-ui/icons'

import { ProfileObject } from 'src/dataSources/geo/CrossSection'
import { RootState } from 'src/Store'
import { profileActions } from 'src/actions/ProfileActions'
import { GeoModel } from 'src/dataSources/geo/GeoModel'
import { CallResult } from 'src/CustomTypes'

import LayerLegend from '../layerpicker/layerlegend/LayerLegend'

import { profileInfoStyle, ProfileInfoStyleProps } from './ProfileInfo.style'
import { appStyle } from 'src/App.style'
import { rgbToHex } from 'src/utils/Utils'
import { boreholeInfoActions } from 'src/actions/BoreholeInfoActions'
import { infoMenuActions } from 'src/actions/InfoMenuActions'
import { InfoView } from 'src/reducers/InfoMenuReducer'
import { ProfileSetting } from 'src/reducers/ProfileReducer'
import { numberToDecimalString } from '../../utils/Utils'
import { LayerParams } from 'src/dataSources/layers/LayerRegistry'

/**
 * ProfileInfoProps contains properties for the component.
 */
export interface ProfileInfoProps {
  selectedProfile: ProfileObject | undefined
}

/**
 * ProfileInfo figure and legend for the current profile.
 * @param props Properties for component.
 * @returns JSX component.
 */
export const ProfileInfo: FC<ProfileInfoProps> = (props: ProfileInfoProps) => {
  const appClasses = appStyle()
  const dispatch = useDispatch()

  const [minX, setMinX] = useState(0)
  const [maxX, setMaxX] = useState(0)
  const [minY, setMinY] = useState(0)
  const [maxY, setMaxY] = useState(0)

  const [crossX, setCrossX] = useState(0)
  const [crossY, setCrossY] = useState(0)
  const [showCrosshair, setShowCrosshair] = useState(false)
  const [pixelScaleX, setPixelScaleX] = useState(0)
  const [pixelScaleY, setPixelScaleY] = useState(0)

  const [valueX, setValueX] = useState(0)
  const [valueY, setValueY] = useState(0)

  const crosshairContainerRef = useRef<HTMLDivElement>(null)
  const crosshairValueXCardRef = useRef<HTMLDivElement>(null)
  const crosshairValueYCardRef = useRef<HTMLDivElement>(null)

  const bottomBarWidth = useSelector((state: RootState): number => state.generalState.bottomBarWidth)
  const bottomBarHeight = useSelector((state: RootState): number => state.generalState.bottomBarHeight)
  const layerParams = useSelector((state: RootState): LayerParams[] => state.mapState.layerParams)
  const maxDepth = useSelector((state: RootState): number => state.profileState.maxDepth)
  const enabledProfileSettings = useSelector((state: RootState): ProfileSetting[] => state.profileState.enabledSettings)
  const selectedModel = useSelector((state: RootState): GeoModel | undefined => state.generalState.selectedGeoModel)
  const fetchProfileStatus = useSelector((state: RootState): CallResult => state.profileState.fetchProfileStatus)
  const fetched = fetchProfileStatus === CallResult.Succeeded
  const error = fetchProfileStatus === CallResult.Failed
  const running = fetchProfileStatus === CallResult.Running

  const piProps: ProfileInfoStyleProps = {
    bottomBarHeight: bottomBarHeight
  }
  const classes = profileInfoStyle(piProps)

  /**
   * Is a given profile setting enabled?
   * @param setting Layer to check.
   * @returns true if layer is selected, false otherwise.
   */
  const isProfileSettingEnabled = (setting: ProfileSetting): boolean => {
    return enabledProfileSettings.some(enabledSetting => enabledSetting === setting)
  }

  const onMouseOver = (e: React.MouseEvent<HTMLElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left - minX
    const y = e.clientY - rect.top

    if (crosshairContainerRef.current?.clientHeight !== undefined) {
      const graphHeight = crosshairContainerRef.current?.clientHeight

      if (
        x >= minX &&
        x <= maxX &&
        y >= minY &&
        y <= maxY) {
        setShowCrosshair(true)

        const graphX = x - minX
        const graphY = y - minY
        setCrossX(graphX)
        setCrossY(graphY)

        setValueX((graphX + 2) * pixelScaleX)
        setValueY(maxDepth + ((graphHeight - y + minY) * pixelScaleY))
      } else {
        setShowCrosshair(false)
      }
    }
  }

  useEffect(() => {
    dispatch(profileActions.setCrosshairDistance(showCrosshair ? valueX : undefined))
    // eslint-disable-next-line
  }, [valueX, showCrosshair])

  useEffect(() => {
    applyProfileViewSettings()
    // eslint-disable-next-line
  }, [enabledProfileSettings])

  useEffect(() => {
    extractGraphBoundsFromSVG()
    extractPixelScalesFromSVG()

    const boringer = document.getElementsByClassName('boring-container')
    for (let i = 0; i < boringer.length; i++) {
      const boring = boringer[i]
      boringer[i].addEventListener('click', () => {
        const id = boring.getAttribute('data-id')
        if (id !== null) {
          dispatch(infoMenuActions.currentView(InfoView.BoreholeInfo))
          dispatch(boreholeInfoActions.setBoreholeId(+id))
        }
      })
    }

    // Tests extracting the color of a soil layer in the svg
    const test = document.getElementsByClassName('geounit-1078')
    if (test.length > 0) {
      const rgbComponents = getComputedStyle(test[0]).fill.replace('rgb(', '').replace(')', '').split(',')
      console.log(rgbToHex(+rgbComponents[0], +rgbComponents[1], +rgbComponents[2]))
    }

    // Apply view settings
    applyProfileViewSettings()

    // eslint-disable-next-line
  }, [fetchProfileStatus])

  /**
   * Apply the view settings for the profile tool.
   */
  const applyProfileViewSettings = (): void => {
    // Hide boring labels
    const labels = Array.from(document.querySelectorAll<HTMLElement>('[class*=geodatasource]'))
    for (const label of labels) {
      label.style.display = isProfileSettingEnabled(ProfileSetting.SHOW_BOREHOLE_LABEL) ? 'block' : 'none'
    }

    // Hide geology labels
    const geologyLabels = Array.from(document.getElementsByClassName('sample-text') as HTMLCollectionOf<HTMLElement>)
    for (const label of geologyLabels) {
      label.style.display = isProfileSettingEnabled(ProfileSetting.SHOW_GEOLOGY_LABEL) ? 'block' : 'none'
    }

    // Hide filter levels
    const filterLevels = Array.from(document.getElementsByClassName('screen') as HTMLCollectionOf<HTMLElement>)
    for (const level of filterLevels) {
      level.style.display = isProfileSettingEnabled(ProfileSetting.SHOW_FILTER_LEVEL) ? 'block' : 'none'
    }

    // Hide water levels
    const waterLevels = Array.from(document.getElementsByClassName('screen-text') as HTMLCollectionOf<HTMLElement>)
    for (const level of waterLevels) {
      level.style.display = isProfileSettingEnabled(ProfileSetting.SHOW_WATER_LEVEL) ? 'block' : 'none'
    }

    // Hide water level symbols
    const waterLevelSymbols = Array.from(document.getElementsByClassName('screen-mark') as HTMLCollectionOf<HTMLElement>)
    for (const symbol of waterLevelSymbols) {
      symbol.style.display = isProfileSettingEnabled(ProfileSetting.SHOW_WATER_LEVEL_SYMBOL) ? 'block' : 'none'
    }

    // Hide Geo logo
    const logo = document.getElementById('logo')
    if (logo !== null) {
      logo.style.display = isProfileSettingEnabled(ProfileSetting.SHOW_GEO_LOGO) ? 'block' : 'none'
    }
  }

  /**
   * Finds the bounding box for the part of the svg containg the actual graph and saves it to local state.
   * Excluding everything outside the axis lines (eg. tics)
   */
  const extractGraphBoundsFromSVG = (): void => {
    const svg = document.getElementById('svg1')
    if (svg !== null) {
      const axis = svg.querySelectorAll(':scope > .axis')
      const fistVirticalAxis = axis.item(0)
      if (fistVirticalAxis !== null) {
        const x1 = fistVirticalAxis.getAttribute('x1')
        const y1 = fistVirticalAxis.getAttribute('y1')
        const y2 = fistVirticalAxis.getAttribute('y2')
        setMinX(x1 !== null ? parseInt(x1) : 0)
        setMinY(y2 !== null ? parseInt(y2) : 0)
        setMaxY(y1 !== null ? parseInt(y1) : 0)
      }
      const lastVirticalAxis = axis.item(1)
      if (lastVirticalAxis !== null) {
        const x2 = lastVirticalAxis.getAttribute('x2')
        setMaxX(x2 !== null ? parseInt(x2) : 0)
      }
    }
  }

  /**
   * Finds for each axis the scale factor between a pixel and 1 unit on the axis.
   * Saves the scale factor to local state.
   */
  const extractPixelScalesFromSVG = (): void => {
    const gridValuesX: Map<number, number> = new Map()
    const gridValuesY: Map<number, number> = new Map()
    const axisGrid = document.getElementById('id-axis-grid')
    const axisTexts = axisGrid?.getElementsByClassName('axis-text')
    if (axisTexts !== undefined) {
      for (let i = 0; i < axisTexts.length; i++) {
        const anchor = axisTexts[i].getAttribute('text-anchor')
        const gridValue: number = +axisTexts[i].innerHTML
        const anchorPosition = anchor === 'middle' ? axisTexts[i].getAttribute('x') : axisTexts[i].getAttribute('y')
        if (anchorPosition !== null) {
          anchor === 'middle' ? gridValuesX.set(gridValue, +anchorPosition) : gridValuesY.set(gridValue, +anchorPosition)
        }
      }
      if (gridValuesX.size > 1) {
        setPixelScaleX(calculatePixelScale(gridValuesX))
      }
      if (gridValuesY.size > 1) {
        setPixelScaleY(calculatePixelScale(gridValuesY))
      }
    }
  }

  /**
   * Calculates and returns the pixel scale describing the number of pixels between two values on the axis.
   * @param map containing pairs of axis value (key) and the values pixel position in the svg (value)
   * @returns the pixel scale describing the number of pixels between two values on the axis.
   */
  const calculatePixelScale = (map: Map<number, number>): number => {
    const keys: number[] = []
    const values: number[] = []
    map.forEach((v, k) => {
      keys.push(k)
      values.push(v)
    })
    const sortedKeys: number[] = keys.sort((k1, k2) => k2 - k1)
    const sortedValues: number[] = values.sort((v1, v2) => v2 - v1)
    const absGridValueDiff = Math.abs(sortedKeys[0] - sortedKeys[1])
    const absGridPixelDiff = Math.abs(sortedValues[0] - sortedValues[1])

    return absGridValueDiff / absGridPixelDiff
  }

  const [updatingProfile, setUpdatingProfile] = useState<number | undefined>(undefined)
  const startUpdateProfileInterval = (): void => {
    if (updatingProfile !== undefined) {
      clearTimeout(updatingProfile)
      setUpdatingProfile(undefined)
    }
    const timeout = window.setTimeout(() => {
      dispatch(profileActions.fetchProfile())
      setUpdatingProfile(undefined)
    }, 500)
    setUpdatingProfile(timeout)
  }
  useEffect(() => {
    dispatch(profileActions.fetchingProfileStarted())
    startUpdateProfileInterval()
    // eslint-disable-next-line
  }, [bottomBarWidth, bottomBarHeight, selectedModel, layerParams])

  // Remove svg background color as it will apply to all svg elements on page
  let svg = ''
  if (props.selectedProfile?.Svg !== undefined) {
    svg = props.selectedProfile.Svg.replace('background-color: white;', '')
  }
  return (
    <div>
      {running && (
        <div className={classes.progress}>
          <CircularProgress size={30} className={appClasses.progressIcon} />
          <FormattedMessage id='common.loading' />
        </div>
      )}
      {error && (
        <div className={appClasses.errorMessage}>
          <Error fontSize='large' className={appClasses.errorIcon} />
          <FormattedMessage id='common.loading.error' />
        </div>
      )}
      {fetched && (
        <div className={classes.root}>
          {props.selectedProfile?.Svg !== undefined && (
            <div>
              <div
                onMouseMove={onMouseOver}
                className={classes.profileWrapper}
              >
                <div className={classes.figure} dangerouslySetInnerHTML={{ __html: svg }} />
                <div
                  className={classes.crosshairContainer}
                  ref={crosshairContainerRef}
                  style={{ width: maxX - minX, height: maxY - minY, marginTop: minY, marginLeft: minX * 2 }}
                >
                  <div
                    className={classes.crosshairHorizontal}
                    style={{ opacity: showCrosshair ? 1 : 0, top: crossY }}
                  />
                  <div
                    className={classes.crosshairVertical}
                    style={{ opacity: showCrosshair ? 1 : 0, left: crossX, top: 2 }}
                  />
                  <div
                    ref={crosshairValueXCardRef}
                    className={classes.crosshairValueXCard}
                    style={{
                      opacity: showCrosshair ? 1 : 0,
                      left: `${crossX - (crosshairValueXCardRef.current?.clientWidth === undefined ? 0 : crosshairValueXCardRef.current?.clientWidth / 2)}px`
                    }}
                  >
                    <div className={classes.triangleTop} />
                    <div className={classes.crosshairValueX}>
                      <p className={classes.crosshairValue}>{numberToDecimalString(valueX, 0)}</p>
                    </div>
                  </div>
                  <div
                    ref={crosshairValueYCardRef}
                    className={classes.crosshairValueYCard}
                    style={{
                      opacity: showCrosshair ? 1 : 0,
                      top: `${crossY - (crosshairValueYCardRef.current?.clientHeight === undefined ? 0 : crosshairValueYCardRef.current?.clientHeight / 2)}px`
                    }}
                  >
                    <div className={classes.crosshairValueY}>
                      <p className={classes.crosshairValue}>{numberToDecimalString(valueY, 1)}</p>
                    </div>
                    <div className={classes.triangleRight} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={classes.legend}>
            <LayerLegend numberOfColumns={1} layerPickerStyle={false} />
          </div>
        </div>
      )}
    </div>
  )
}
